import { useState, type FormEvent } from 'react';
import { ContactNote, isValidPan, type StartIdentificationRequest } from '@asko/shared';
import { ApiError } from '../lib/api';
import { formatExpiry, formatPan, normalizePassport, formatPhone } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { Checkbox } from './Checkbox';
import { ContactsFields, EMPTY_CONTACT, type ContactDraft } from './ContactsFields';
import { DateField } from './DateField';
// SMS-tasdiq vaqtincha olib turildi (14.09.2026) — qaytarilganda kerak
// import { PhoneVerify } from './PhoneVerify';

/**
 * Mijoz anketasi (BT §7.2, 1-ekran).
 *
 * MUHIM: MyID Web SDK tug'ilgan sana va (JSHSHIR yoki pasport) bo'lmasa
 * ishga tushmaydi — shuning uchun ular shu yerda yig'iladi, MyID'dan oldin.
 * Qolgan ma'lumotlar (F.I.Sh, manzil) MyID'dan keladi, qo'lda kiritilmaydi.
 */

interface AnketaFormProps {
  onSubmit: (data: StartIdentificationRequest) => Promise<void>;
  error: ApiError | null;
  submitting: boolean;
}

type IdentityKind = 'pinfl' | 'passport';

export function AnketaForm({ onSubmit, error, submitting }: AnketaFormProps) {
  const { t, apiMessage } = useI18n();

  const [phone, setPhone] = useState('998');
  // SMS-tasdiq vaqtincha olib turildi (mijoz qarori, 14.09.2026):
  // sotuvchi raqamni shunchaki kiritadi. Qaytarilganda bu yerga
  // `const [phoneToken, setPhoneToken] = useState<string | null>(null);`
  // qaytadi — server tomonda tekshiruv joyida turibdi.
  const [kind, setKind] = useState<IdentityKind>('pinfl');
  const [pinfl, setPinfl] = useState('');
  const [passport, setPassport] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [pan, setPan] = useState('');
  const [expiry, setExpiry] = useState('');
  const [isResident, setIsResident] = useState(true);
  // Alif kamida 2 ta kontakt talab qiladi — shuncha qator darrov ko'rsatiladi
  const [contacts, setContacts] = useState<ContactDraft[]>([
    { ...EMPTY_CONTACT },
    { ...EMPTY_CONTACT, note: ContactNote.SIBLINGS },
  ]);

  /*
    SMS-tasdiq qaytarilganda bu ham qaytadi: server tasdiqni rad etsa,
    ekranda «✓ Tasdiqlandi» qolib ketmasligi kerak edi.

    useEffect(() => {
      if (error?.fieldError('phoneVerificationToken')) setPhoneToken(null);
    }, [error]);
  */

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    void onSubmit({
      phone,
      birth_date: birthDate,
      card: { pan, expiry },
      is_resident: isResident,
      contacts,
      ...(kind === 'pinfl' ? { pinfl } : { pass_data: passport }),
    } as StartIdentificationRequest);
  }

  const fieldError = (name: string) => error?.fieldError(name);

  /** Ekranda ko'rsatiladigan maydonlar — qolgani alohida chiqariladi. */
  const SHOWN = [
    'phone',
    'phoneVerificationToken',
    'pinfl',
    'pass_data',
    'birth_date',
    'card.pan',
    'card.expiry',
    'contacts',
  ];
  const unmatchedErrors = Object.entries(error?.details ?? {})
    .filter(([key]) => !SHOWN.includes(key) && !key.startsWith('contacts.'))
    .flatMap(([, messages]) => messages);

  return (
    <form className="card" onSubmit={handleSubmit} noValidate>
      <h2 className="section-title">{t('anketa.title')}</h2>
      <p className="section-hint">{t('anketa.hint')}</p>

      {/*
        SMS-tasdiq vaqtincha olib turildi (mijoz qarori, 14.09.2026).
        Qaytarish uchun quyidagi blokni ochib, pastdagi oddiy maydonni
        olib tashlash kifoya:

        <PhoneVerify
          phone={phone}
          onPhoneChange={setPhone}
          token={phoneToken}
          onVerified={(tok) => setPhoneToken(tok || null)}
          fieldError={fieldError('phone') ?? fieldError('phoneVerificationToken')}
          disabled={submitting}
        />
      */}
      <label className="field field--phone-plain">
        <span className="field__label">{t('phone.label')}</span>
        <input
          className="field__input field__input--mono"
          inputMode="numeric"
          placeholder="998 90 123 45 67"
          value={formatPhone(phone)}
          onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 12))}
          aria-invalid={Boolean(fieldError('phone'))}
          disabled={submitting}
          required
        />
        {fieldError('phone') && <span className="field__error">{fieldError('phone')}</span>}
      </label>

      <fieldset className="field">
        <legend className="field__label">{t('anketa.identity.legend')}</legend>
        <div className="segmented" role="radiogroup">
          <button
            type="button"
            role="radio"
            aria-checked={kind === 'pinfl'}
            className={`segmented__btn ${kind === 'pinfl' ? 'is-active' : ''}`}
            onClick={() => setKind('pinfl')}
          >
            {t('anketa.identity.pinfl')}
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={kind === 'passport'}
            className={`segmented__btn ${kind === 'passport' ? 'is-active' : ''}`}
            onClick={() => setKind('passport')}
          >
            {t('anketa.identity.passport')}
          </button>
        </div>
      </fieldset>

      <div className="field-row">
        {kind === 'pinfl' ? (
          <label className="field field--pinfl">
            <span className="field__label">{t('anketa.pinfl.label')}</span>
            <input
              className="field__input"
              inputMode="numeric"
              placeholder="31234567890123"
              value={pinfl}
              onChange={(e) => setPinfl(e.target.value.replace(/\D/g, '').slice(0, 14))}
              aria-invalid={Boolean(fieldError('pinfl'))}
              required
            />
            {fieldError('pinfl') && <span className="field__error">{fieldError('pinfl')}</span>}
          </label>
        ) : (
          <label className="field field--passport">
            <span className="field__label">{t('anketa.passport.label')}</span>
            <input
              className="field__input field__input--mono"
              placeholder="AA1234567"
              value={passport}
              onChange={(e) => setPassport(normalizePassport(e.target.value))}
              aria-invalid={Boolean(fieldError('pass_data'))}
              required
            />
            {fieldError('pass_data') && (
              <span className="field__error">{fieldError('pass_data')}</span>
            )}
          </label>
        )}

        <DateField
          label={t('anketa.birthDate')}
          value={birthDate}
          onChange={setBirthDate}
          error={fieldError('birth_date')}
          required
        />
      </div>

      <div className="field-row">
        <label className="field field--card">
          <span className="field__label">{t('anketa.card')}</span>
          <input
            className="field__input field__input--mono"
            inputMode="numeric"
            placeholder="8600 1234 5678 3456"
            value={formatPan(pan)}
            onChange={(e) => setPan(e.target.value.replace(/\D/g, '').slice(0, 16))}
            aria-invalid={Boolean(fieldError('card.pan'))}
            required
          />
          {fieldError('card.pan') ? (
            <span className="field__error">{fieldError('card.pan')}</span>
          ) : (
            // Taqiq emas, ogohlantirish: xato terilgan karta provayderda
            // rad etiladi, va bu PULLIK identifikatsiyadan KEYIN bo'ladi.
            pan.length === 16 &&
            !isValidPan(pan) && <span className="field__warn">{t('anketa.panSuspect')}</span>
          )}
        </label>

        <label className="field field--expiry">
          <span className="field__label">{t('anketa.expiry')}</span>
          <input
            className="field__input field__input--mono"
            inputMode="numeric"
            placeholder="12/30"
            value={formatExpiry(expiry)}
            onChange={(e) => setExpiry(e.target.value.replace(/\D/g, '').slice(0, 4))}
            aria-invalid={Boolean(fieldError('card.expiry'))}
            required
          />
          {fieldError('card.expiry') && (
            <span className="field__error">{fieldError('card.expiry')}</span>
          )}
        </label>
      </div>

      <ContactsFields
        contacts={contacts}
        clientPhone={phone}
        onChange={setContacts}
        error={fieldError('contacts')}
        fieldError={fieldError}
      />

      <Checkbox checked={!isResident} onChange={(checked) => setIsResident(!checked)}>
        {t('anketa.nonResident')}
      </Checkbox>

      {/*
        Ba'zi xatolar hech qaysi maydonga tegishli emas (masalan, butun
        so'rov qabul qilinmasa). Ilgari ular ko'rinmasdi: yuqorida
        «maydonlar to'g'ri to'ldirilmagan» yozuvi turardi, qizil esa
        hech qayerda yo'q edi — sotuvchi nima qilishni bilmasdi
        (jonli sinov, 25.08).
      */}
      {unmatchedErrors.length > 0 && (
        <div className="alert alert--error" role="alert">
          {unmatchedErrors.map((message) => (
            <div key={message}>{message}</div>
          ))}
        </div>
      )}

      {error && !error.details && (
        <div className="alert alert--error" role="alert">
          {apiMessage(error)}
        </div>
      )}

      {/* SMS-tasdiq qaytarilganda tugma yana `!phoneToken` bilan
          bloklanadi va pastda `anketa.phoneNote` izohi chiqadi. */}
      <button className="btn btn--primary btn--wide" type="submit" disabled={submitting}>
        {submitting ? t('anketa.submitting') : t('anketa.submit')}
      </button>
    </form>
  );
}
